"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAccrualCalculator = exports.getDaysInYear = exports.getDaysInMonth = exports.calculateProjectedInterest = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const calculateProjectedInterest = ({ balance, dayCountConvention, targetFundingDate, maturityDate, allInRate, }) => {
    let dayCount = 360;
    if (dayCountConvention === 'Actual / 365') {
        dayCount = 365;
    }
    const days = Math.abs(common_1.date.diffDays(targetFundingDate, maturityDate));
    const projectedInterest = balance * (allInRate / dayCount) * days;
    return projectedInterest;
};
exports.calculateProjectedInterest = calculateProjectedInterest;
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
exports.getDaysInMonth = getDaysInMonth;
// Can be tested later, no one is using Actual / Actual yet
const getDaysInYear = (year) => (year % 4 === 0 && year % 100 > 0) || year % 400 === 0 ? 366 : 365;
exports.getDaysInYear = getDaysInYear;
// Returns a weighted percentage corresponding to the day in question
// Example: 30/360
const getDayBasis = (date, basis) => {
    const [year, _month, _day] = date.split('-');
    // const daysInMonth = getDaysInMonth(Number(year), Number(month));
    const daysInYear = (0, exports.getDaysInYear)(Number(year));
    switch (basis) {
        case '30 / 360':
            // TODO: revisit
            return 30 / 360;
        case '30 / 365':
            // TODO: revisit
            return 30 / 365;
        case 'Actual / 360':
            return 1 / 360;
        case 'Actual / 365':
            return 1 / 365;
        case 'Actual / Actual':
            return 1 / daysInYear;
        default:
            return (0, common_1.finleyAssertUnreachable)(basis);
    }
};
// With current expectations, each loan maintains a static balance at funding through maturity
// Accrual calculation does not require transaction history, just loan balances
const getAccrualCalculator = ({ loan, }) => {
    const fees = [];
    const interestPeriods = [];
    const interestRates = loan.pricing.history.map((entry) => ({
        effectiveDate: entry.effectiveDate,
        determinationDate: entry.baseRate.determinationDate,
        value: entry.baseRate.adjustedRate,
    }));
    const fetchBaseRate = (currentDate) => {
        const arrayIndex = interestRates.findIndex(({ effectiveDate }) => effectiveDate > currentDate);
        if (arrayIndex < 0) {
            // Use last known rate
            return interestRates[interestRates.length - 1]?.value ?? 0;
        }
        // Use element right before which matches expected effective period
        return interestRates[arrayIndex - 1]?.value ?? 0;
    };
    // Interest periods
    // Skip this for now because invoice handling makes payment date management redundant
    // > expand interest.period config using end dates and period shift if defined
    return {
        fees,
        interestPeriods,
        interestRates,
        calculateInterest: ({ longTermDebtBalances, period, logFunc }) => {
            const logInfo = (message, level) => {
                if (logFunc)
                    logFunc({ message }, level ?? finley_types_1.Log.LogLevel.Info);
            };
            const accruals = [];
            const { targetFundingDate } = loan.dates;
            const startDate = period.startDate ?? targetFundingDate;
            const endDate = period.endDate;
            // Noting that accruing interest past maturity date is technically allowed (per product),
            // as long as the loan still has principal balance outstanding, thus no assertion here
            (0, common_1.finleyAssert)(startDate <= endDate, `Accrual period start date (${startDate}) must be on or before end date ${endDate}`);
            (0, common_1.finleyAssert)(startDate >= targetFundingDate, `Accrual period start date (${startDate}) must be on or after loan funding date ${targetFundingDate}`);
            logInfo(`Interest Accrual: attempting to accrue interest for [startDate:${startDate}] to [endDate:${endDate}] for [loadId:${loan.id}]`);
            for (const lenderShareItem of longTermDebtBalances) {
                const doesShareExistOnLoan = loan.funding.lenderShares.some((ls) => ls.lenderShareId === lenderShareItem.lenderShareId);
                (0, common_1.finleyAssert)(doesShareExistOnLoan, `Received lender share from entry balances that is not part of loan [inputLenderShareId:${lenderShareItem.lenderShareId}] [loanId:${loan.id}]`);
                if (lenderShareItem.balanceDateMap.size === 0) {
                    logInfo(`Interest Accrual: had 0 balance activity so no-op for [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}]`);
                    continue;
                }
                const marginRate = loan.pricing.margins.find(({ organizationId }) => organizationId === lenderShareItem.capitalProviderId)?.rate ?? 0;
                let runningBalance = 0;
                let currentDate = startDate;
                // eslint-disable-next-line no-restricted-syntax
                while (currentDate < endDate) {
                    const fetchedBalance = lenderShareItem.balanceDateMap.get(currentDate);
                    runningBalance += fetchedBalance ?? 0;
                    // No need for accrual activity if there's 0 balance (i.e., no interest to accrue)
                    if (runningBalance === 0) {
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    const baseRate = fetchBaseRate(currentDate);
                    const allInRate = baseRate + marginRate;
                    const dayBasis = getDayBasis(currentDate, loan.pricing.accrual.basis);
                    const numberOfDays = 1; // Fixed daily accrual - as the calculator evolves we'll support more options
                    const value = runningBalance * allInRate * dayBasis * numberOfDays;
                    accruals.push({
                        tAccount: finley_types_1.Accounting.CurrentLiabilityAccounts.AccruedInterest,
                        creditFacilityId: loan.facility.creditFacilityId,
                        organizationId: lenderShareItem.capitalProviderId,
                        lenderShareId: lenderShareItem.lenderShareId,
                        date: currentDate,
                        value,
                        proforma: false,
                        variables: [
                            { name: 'principalBalance', value: runningBalance },
                            { name: 'baseRate', value: baseRate },
                            { name: 'marginRate', value: marginRate },
                            { name: 'allInRate', value: allInRate },
                            { name: 'dayBasis', value: dayBasis },
                            { name: 'numberOfDays', value: numberOfDays },
                        ], // TODO: @sachinr4 to save to database for additional visibility with accrual management
                    });
                    // Reconcile against accrual value for overall loan
                    // Possible rounding variances may exist between loan level accrual and sum of share accruals
                    currentDate = common_1.date.addDays(currentDate, 1);
                }
            }
            logInfo(`Interest Accrual: generated ${accruals.length} accrual entries for [loadId:${loan.id}]`);
            return accruals;
        },
        calculateInterestForAdminAgentPrincipal: ({ adminAgentLenderShare, adminAgentPrincipalBalances, period, logFunc, }) => {
            const logInfo = (message, level) => {
                if (logFunc)
                    logFunc({ message }, level ?? finley_types_1.Log.LogLevel.Info);
            };
            const { targetFundingDate } = loan.dates;
            const startDate = period.startDate ?? targetFundingDate;
            const endDate = period.endDate;
            // Noting that accruing interest past maturity date is technically allowed (per product),
            // as long as the loan still has principal balance outstanding, thus no assertion here
            (0, common_1.finleyAssert)(startDate <= endDate, `Accrual period start date (${startDate}) must be on or before end date ${endDate}`);
            (0, common_1.finleyAssert)(startDate >= targetFundingDate, `Accrual period start date (${startDate}) must be on or after loan funding date ${targetFundingDate}`);
            logInfo(`Admin Agent Interest Accrual: attempting to accrue interest for [startDate:${startDate}] to [endDate:${endDate}] for [loadId:${loan.id}]`);
            const accruals = [];
            for (const lenderShareItem of adminAgentPrincipalBalances) {
                if (lenderShareItem.capitalProviderId ===
                    adminAgentLenderShare.capitalProviderId) {
                    logInfo(`Admin Agent Interest Accrual: came across Admin Agent's own lender share when attempting to accrue interest on fronted balance so no-op for [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}]`, finley_types_1.Log.LogLevel.Warn);
                    continue;
                }
                if (lenderShareItem.balanceDateMap.size === 0) {
                    logInfo(`Admin Agent Interest Accrual: had 0 balance activity so no-op for [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}]`);
                    continue;
                }
                const marginRate = loan.pricing.margins.find(({ organizationId }) => organizationId === lenderShareItem.capitalProviderId)?.rate ?? 0;
                let runningBalance = 0;
                let currentDate = startDate;
                // eslint-disable-next-line no-restricted-syntax
                while (currentDate < endDate) {
                    const fetchedBalance = lenderShareItem.balanceDateMap.get(currentDate);
                    runningBalance += fetchedBalance ?? 0;
                    // No need for accrual activity if there's 0 balance (i.e., no interest to accrue)
                    if (runningBalance === 0) {
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    const baseRate = fetchBaseRate(currentDate);
                    const allInRate = baseRate + marginRate;
                    const dayBasis = getDayBasis(currentDate, loan.pricing.accrual.basis);
                    const numberOfDays = 1; // Fixed daily accrual - as the calculator evolves we'll support more options
                    const interest = runningBalance * allInRate * dayBasis * numberOfDays;
                    accruals.push({
                        tAccount: finley_types_1.Accounting.CurrentLiabilityAccounts.AccruedInterest,
                        creditFacilityId: loan.facility.creditFacilityId,
                        // Important to note that the "Admin Agent" should be the one
                        // receiving all of these interest accruals rather than the lender
                        // that owes the fronted money
                        organizationId: adminAgentLenderShare.capitalProviderId,
                        lenderShareId: adminAgentLenderShare.id,
                        date: currentDate,
                        value: interest,
                        proforma: false,
                        variables: [
                            { name: 'adminAgentPrincipalBalance', value: runningBalance },
                            { name: 'baseRate', value: baseRate },
                            { name: 'marginRate', value: marginRate },
                            { name: 'allInRate', value: allInRate },
                            { name: 'dayBasis', value: dayBasis },
                            { name: 'numberOfDays', value: numberOfDays },
                        ], // TODO: @sachinr4 to save to database for additional visibility with accrual management
                    });
                    // Reconcile against accrual value for overall loan
                    // Possible rounding variances may exist between loan level accrual and sum of share accruals
                    currentDate = common_1.date.addDays(currentDate, 1);
                }
            }
            logInfo(`Admin Agent Interest Accrual: generated ${accruals.length} accrual entries for [loadId:${loan.id}]`);
            return accruals;
        },
    };
};
exports.getAccrualCalculator = getAccrualCalculator;
//# sourceMappingURL=accrual.js.map